import type { Question, Survey, SurveyMetadata } from '../../types';
import { ValidationService, ErrorService } from '../core';
import { AnswerExtractor } from '../../utils/AnswerExtractor';

export interface SurveyState {
  currentQuestionIndex: number;
  answers: Record<string, string | string[] | null>;
  currentSurvey: Survey | null;
  surveyMetadata: SurveyMetadata | null;
  showingIntro: boolean;
}

export class SurveyStateManager {
  private state: SurveyState;

  constructor() {
    this.state = {
      currentQuestionIndex: 0,
      answers: {},
      currentSurvey: null,
      surveyMetadata: null,
      showingIntro: true
    };
  }

  // Getters
  getCurrentQuestionIndex(): number {
    return this.state.currentQuestionIndex;
  }

  getAnswers(): Record<string, string | string[] | null> {
    return this.state.answers;
  }

  getCurrentSurvey(): Survey | null {
    return this.state.currentSurvey;
  }

  getSurveyMetadata(): { name: string; description: string } | null {
    return this.state.surveyMetadata;
  }

  isShowingIntro(): boolean {
    return this.state.showingIntro;
  }

  // Setters
  setCurrentSurvey(survey: Survey): void {
    this.state.currentSurvey = survey;
  }

  setSurveyMetadata(metadata: { name: string; description: string }): void {
    this.state.surveyMetadata = metadata;
  }

  setShowingIntro(showing: boolean): void {
    this.state.showingIntro = showing;
  }

  // Navigation methods
  previousQuestion(): boolean {
    this.saveCurrentAnswer();
    ErrorService.clearError();
    
    if (this.state.currentQuestionIndex > 0) {
      this.state.currentQuestionIndex--;
      return true;
    } else if (this.state.currentQuestionIndex === 0) {
      // Go back to intro page
      this.state.showingIntro = true;
      return true;
    }
    return false;
  }

  nextQuestion(): boolean {
    this.saveCurrentAnswer();
    
    const question = this.getCurrentQuestion();
    // Get current answer from DOM, fallback to saved answer
    const currentAnswer = AnswerExtractor.extractAnswer(question.type) ?? this.state.answers[question.id];
    const validation = ValidationService.validateQuestion(question, currentAnswer);
    
    if (!validation.isValid) {
      ErrorService.showError(validation.message!);
      return false;
    }

    ErrorService.clearError();
    if (this.state.currentSurvey && this.state.currentQuestionIndex < this.state.currentSurvey.questions.length - 1) {
      this.state.currentQuestionIndex++;
      return true;
    }
    return false;
  }

  // Answer management
  private saveCurrentAnswer(): void {
    const question = this.getCurrentQuestion();
    const answerValue = AnswerExtractor.extractAnswer(question.type);
    
    // Always save the answer, even if null (which means no selection for radio/checkboxes)
    this.state.answers[question.id] = answerValue;
  }

  public saveCurrentAnswerForSubmission(): void {
    this.saveCurrentAnswer();
  }

  getCurrentQuestion(): Question {
    if (!this.state.currentSurvey) {
      throw new Error('Survey not loaded');
    }
    return this.state.currentSurvey.questions[this.state.currentQuestionIndex];
  }

  // Reset state
  reset(): void {
    this.state = {
      currentQuestionIndex: 0,
      answers: {},
      currentSurvey: null,
      surveyMetadata: null,
      showingIntro: true
    };
  }
} 